import { createReducer } from './utils';
import {
  GET_OBJECTIVES_REQUEST,
  GET_OBJECTIVES_SUCCESS,
  GET_OBJECTIVES_FAILURE,
  CREATE_OBJECTIVE_REQUEST,
  CREATE_OBJECTIVE_SUCCESS,
  CREATE_OBJECTIVE_FAILURE,
  UPDATE_OBJECTIVE_REQUEST,
  UPDATE_OBJECTIVE_SUCCESS,
  UPDATE_OBJECTIVE_FAILURE,
  DELETE_OBJECTIVE_REQUEST,
  DELETE_OBJECTIVE_SUCCESS,
  DELETE_OBJECTIVE_FAILURE,
  RESET_OBJECTIVES_STATE,
} from './constants';
import {
  Objective,
  CreateObjectiveInput,
  ObjectiveBatchSaveInput,
  UpdateObjectiveInput,
} from '@shared/schema';
import { ObjectivesState, Action, ThunkAPI } from '../types/state';
import { translate as t } from '@/core/i18n/translate';

const defaultState: ObjectivesState = {
  objectives: [],
  loading: false,
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [GET_OBJECTIVES_REQUEST]: handleGetObjectivesRequest,
  [GET_OBJECTIVES_SUCCESS]: handleGetObjectivesSuccess,
  [GET_OBJECTIVES_FAILURE]: handleGetObjectivesFailure,
  [CREATE_OBJECTIVE_REQUEST]: handleCreateObjectiveRequest,
  [CREATE_OBJECTIVE_SUCCESS]: handleCreateObjectiveSuccess,
  [CREATE_OBJECTIVE_FAILURE]: handleCreateObjectiveFailure,
  [UPDATE_OBJECTIVE_REQUEST]: handleUpdateObjectiveRequest,
  [UPDATE_OBJECTIVE_SUCCESS]: handleUpdateObjectiveSuccess,
  [UPDATE_OBJECTIVE_FAILURE]: handleUpdateObjectiveFailure,
  [DELETE_OBJECTIVE_REQUEST]: handleDeleteObjectiveRequest,
  [DELETE_OBJECTIVE_SUCCESS]: handleDeleteObjectiveSuccess,
  [DELETE_OBJECTIVE_FAILURE]: handleDeleteObjectiveFailure,
  [RESET_OBJECTIVES_STATE]: () => defaultState,
});

function handleGetObjectivesRequest(state: ObjectivesState): ObjectivesState {
  return {
    ...state,
    loading: true,
  };
}

function handleGetObjectivesSuccess(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { objectives: Objective[] } | undefined;
  return {
    ...state,
    objectives: payload?.objectives || [],
    loading: false,
    errorMessage: null,
  };
}

function handleGetObjectivesFailure(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.objectives.fetchError'),
  };
}

function handleCreateObjectiveRequest(state: ObjectivesState): ObjectivesState {
  return {
    ...state,
    loading: true,
  };
}

function handleCreateObjectiveSuccess(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { objective: Objective } | undefined;
  return {
    ...state,
    objectives: payload?.objective ? [...state.objectives, payload.objective] : state.objectives,
    loading: false,
    errorMessage: null,
  };
}

function handleCreateObjectiveFailure(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.objectives.createError'),
  };
}

function handleUpdateObjectiveRequest(state: ObjectivesState): ObjectivesState {
  return {
    ...state,
    loading: true,
  };
}

function handleUpdateObjectiveSuccess(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { objective: Objective } | undefined;
  return {
    ...state,
    objectives: payload?.objective
      ? state.objectives.map((obj) =>
          obj.APObjectiveId === payload.objective.APObjectiveId ? payload.objective : obj,
        )
      : state.objectives,
    loading: false,
    errorMessage: null,
  };
}

function handleUpdateObjectiveFailure(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.objectives.updateError'),
  };
}

function handleDeleteObjectiveRequest(state: ObjectivesState): ObjectivesState {
  return {
    ...state,
    loading: true,
  };
}

function handleDeleteObjectiveSuccess(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { objectiveId: number } | undefined;
  return {
    ...state,
    objectives: payload?.objectiveId
      ? state.objectives.filter((obj) => obj.APObjectiveId !== payload.objectiveId)
      : state.objectives,
    loading: false,
    errorMessage: null,
  };
}

function handleDeleteObjectiveFailure(
  state: ObjectivesState,
  action: Action<unknown>,
): ObjectivesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.objectives.deleteError'),
  };
}

export function resetObjectivesState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: RESET_OBJECTIVES_STATE });
  };
}

export function fetchObjectivesByVersionId(versionId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_OBJECTIVES_REQUEST });
    try {
      const objectives = await dataSource.fetchObjectivesByVersionId(versionId);
      dispatch({
        type: GET_OBJECTIVES_SUCCESS,
        payload: { objectives },
      });
    } catch (error) {
      console.error('Error fetching objectives:', error);
      dispatch({
        type: GET_OBJECTIVES_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.objectives.fetchError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.fetchError'),
        severity: 'error' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      throw error;
    }
  };
}

export function createObjective(data: CreateObjectiveInput) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: CREATE_OBJECTIVE_REQUEST });
    try {
      const objective = await dataSource.createObjective(data);
      dispatch({ type: CREATE_OBJECTIVE_SUCCESS, payload: { objective } });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.createSuccess'),
        severity: 'success' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      return objective;
    } catch (error) {
      console.error('Error creating objective:', error);
      dispatch({
        type: CREATE_OBJECTIVE_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.objectives.createError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.createError'),
        severity: 'error' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      throw error;
    }
  };
}

export function saveObjectivesBatch(versionId: number, data: ObjectiveBatchSaveInput) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_OBJECTIVES_REQUEST });
    try {
      const result = await dataSource.saveObjectivesBatch(versionId, data);
      dispatch({ type: GET_OBJECTIVES_SUCCESS, payload: { objectives: result.objectives } });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.updateSuccess'),
        severity: 'success' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      return result;
    } catch (error) {
      console.error('Error saving objectives batch:', error);
      dispatch({
        type: GET_OBJECTIVES_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.objectives.updateError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.updateError'),
        severity: 'error' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      throw error;
    }
  };
}

export function updateObjective(objectiveId: number, data: UpdateObjectiveInput) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: UPDATE_OBJECTIVE_REQUEST });
    try {
      const objective = await dataSource.updateObjective(objectiveId, data);
      dispatch({ type: UPDATE_OBJECTIVE_SUCCESS, payload: { objective } });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.updateSuccess'),
        severity: 'success' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      return objective;
    } catch (error) {
      console.error('Error updating objective:', error);
      dispatch({
        type: UPDATE_OBJECTIVE_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.objectives.updateError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.updateError'),
        severity: 'error' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      throw error;
    }
  };
}

export function deleteObjective(objectiveId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: DELETE_OBJECTIVE_REQUEST });
    try {
      await dataSource.deleteObjective(objectiveId);
      dispatch({ type: DELETE_OBJECTIVE_SUCCESS, payload: { objectiveId } });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.deleteSuccess'),
        severity: 'success' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
    } catch (error) {
      console.error('Error deleting objective:', error);
      dispatch({
        type: DELETE_OBJECTIVE_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.objectives.deleteError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.objectives.deleteError'),
        severity: 'error' as const,
      };
      dispatch({ type: 'SNACK_SET', payload: { snack } });
      throw error;
    }
  };
}
